import { isNuovaDocument } from './nv-layer-stack.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — «UI nuova» page scroll on navigation (MOBILE-NATIVE-PLAN W1.5).
 *              Back / Forward lands where the page was left (the list row you opened);
 *              any other navigation starts at the top, instantly (a smooth scroll showed
 *              the old page sliding away on phones).
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      the router service (routeWillChange / routeDidChange), window popstate,
 *              lib/nv-layer-stack (isNuovaDocument)
 *   siblings:  services/ui.js (installs when the nuova is on); beebee-base-template
 *              routes/application.js _scrollAllToTop and lib/router-tracking.js step aside
 *              in the nuova (this owns the page scroll there); lib/nv-layer-history
 *              (sets history.scrollRestoration = 'manual', swallows the overlay Backs —
 *              they never reach the listener here)
 *   tests:     medical tests/unit/lib/nv-scroll-restore (unit)
 *   gotchas:   (1) Anchor = the tapped list row (a.nv-lt-title href): Back scrolls so that
 *              row sits at the height it had when tapped, whatever the content above it
 *              measures now; the saved offset is the fallback when the row is gone.
 *              (2) The list renders after routeDidChange and grows in chunks: restore
 *              re-aligns each frame until the row stays put, for at most RESTORE_MS.
 *              Touch, wheel, pointer or key during the wait cancels it.
 *              (3) Saved by router.currentURL — the page ON SCREEN (query string
 *              included: page and filters are part of the place). On Back the address
 *              bar already shows the next page at routeWillChange; an aborted
 *              transition leaves currentURL alone.
 *              (4) popstate is heard in CAPTURE: Ember's own listener (registered at
 *              boot, before the nuova is known) starts the transition, so a bubble
 *              listener here would mark it too late.
 *              (5) Every other navigation (links, query-param changes) starts at the top.
 */
const RESTORE_MS = 8000;
const MAX_ENTRIES = 50;
const POP_WINDOW_MS = 1000;
const STEADY_FRAMES = 20;
// a timer, not requestAnimationFrame: rAF stops in a tab that is not painting, and the restore must still land
const FRAME_MS = 16;
// any of these from the user ends an unfinished restore: the page is theirs again
const USER_SCROLL_EVENTS = ['touchstart', 'wheel', 'pointerdown', 'keydown'];
let router = null;
let installed = false;
// a popstate just fired (Ember starts its URL transition synchronously inside it)
let popNavigation = false;
// the transition in flight came from Back / Forward (redirects keep it)
let fromHistory = false;
let restoreFrame = null;
// the list row tapped last ({ href, top, at }): Back puts THAT row back at the same height on screen, whatever the
// content above it measures now (cards, tiles, a loading state)
let tapped = null;
const TAP_FRESH_MS = 5000;
const positions = new Map();

// The page ON SCREEN: the router's URL (updated at routeDidChange, untouched by an aborted transition). On Back the
// address bar already shows the next page when routeWillChange fires, so location cannot tell which page is left.
function currentKey() {
  return router?.currentURL ?? window.location.pathname + window.location.search;
}
function scrollTop() {
  return window.scrollY || document.documentElement?.scrollTop || 0;
}
function cancelRestore() {
  if (restoreFrame !== null) clearTimeout(restoreFrame);
  restoreFrame = null;
  for (const type of USER_SCROLL_EVENTS) window.removeEventListener(type, cancelRestore, true);
}
function anchorRow(href) {
  if (!href) return null;
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(href) : href;
  return document.querySelector(`.nv-lt-row a.nv-lt-title[href="${escaped}"]`)?.closest('.nv-lt-row') ?? null;
}
function restoreTo({
  y: target,
  href,
  top
}) {
  cancelRestore();
  const started = performance.now();
  // the finger / wheel wins over an unfinished restore
  for (const type of USER_SCROLL_EVENTS) window.addEventListener(type, cancelRestore, true);
  let steady = 0;
  const step = () => {
    restoreFrame = null;
    const row = anchorRow(href);
    if (row) {
      const delta = Math.round(row.getBoundingClientRect().top - top);
      if (delta !== 0) window.scrollTo(0, Math.max(0, scrollTop() + delta));
      // re-aligned each frame until the content above it stops moving (tiles / cards finishing their load)
      steady = delta === 0 ? steady + 1 : 0;
      if (steady >= STEADY_FRAMES || performance.now() - started > RESTORE_MS) {
        cancelRestore();
        return;
      }
      restoreFrame = setTimeout(step, FRAME_MS);
      return;
    }
    const max = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo(0, Math.min(target, Math.max(0, max)));
    // no anchor row (yet): keep the offset, wait for the anchor while the list loads
    if (!href && max >= target || performance.now() - started > RESTORE_MS) {
      cancelRestore();
      return;
    }
    restoreFrame = setTimeout(step, FRAME_MS);
  };
  step();
}
function onTap(event) {
  const title = event.target?.closest?.('.nv-lt-row')?.querySelector('a.nv-lt-title[href]');
  if (!title) return;
  const row = title.closest('.nv-lt-row');
  tapped = {
    href: title.getAttribute('href'),
    top: row.getBoundingClientRect().top,
    at: Date.now()
  };
}
function onPopState() {
  popNavigation = true;
  // a pop with no route change (same URL) must not mark a later click as history; Ember may start the URL
  // transition a few ticks after the event, so the mark lives for POP_WINDOW_MS
  setTimeout(() => popNavigation = false, POP_WINDOW_MS);
}
function onRouteWillChange() {
  if (popNavigation) {
    fromHistory = true;
    popNavigation = false;
  }
  if (!isNuovaDocument()) return;
  // the page being left (URL not updated yet); a locked body (sheet open) reports 0 — keep the older value then
  if (document.documentElement.classList.contains('nv-scroll-locked')) return;
  const key = currentKey();
  const fresh = tapped && Date.now() - tapped.at < TAP_FRESH_MS ? tapped : null;
  // a redirect fires routeWillChange again for the same page: keep the anchor it already has
  const previous = positions.get(key);
  positions.delete(key);
  positions.set(key, {
    y: scrollTop(),
    href: fresh?.href ?? previous?.href ?? null,
    top: fresh ? fresh.top : previous?.top ?? 0
  });
  if (positions.size > MAX_ENTRIES) positions.delete(positions.keys().next().value);
}
function onRouteDidChange() {
  const restore = fromHistory;
  fromHistory = false;
  tapped = null;
  if (!isNuovaDocument()) return;
  const saved = restore ? positions.get(currentKey()) : undefined;
  if (saved && (saved.y > 0 || saved.href)) {
    restoreTo(saved);
  } else {
    cancelRestore();
    window.scrollTo(0, 0);
  }
}

/**
 * True when `element` is the page scroller and the nuova owns its scroll on navigation: the shell's legacy
 * «scroll to top» helpers (scroll-top-on-insert modifier, BaseBeebeeComponent.animateTask, socket-tracker) skip it,
 * so a restored Back position is not undone 500 ms later. Inner scrollers keep their behaviour.
 */
function nuovaOwnsPageScroll(element) {
  if (!isNuovaDocument() || typeof document === 'undefined') return false;
  return element === document.documentElement || element === document.body || element === window;
}

/** Installs the navigation scroll handling (idempotent). */
function installScrollRestore(routerService) {
  if (installed || typeof window === 'undefined' || !routerService?.on) return;
  installed = true;
  router = routerService;
  window.addEventListener('popstate', onPopState, true);
  document.addEventListener('click', onTap, true);
  router.on('routeWillChange', onRouteWillChange);
  router.on('routeDidChange', onRouteDidChange);
}

/** Removes it and forgets the saved positions. */
function uninstallScrollRestore() {
  if (!installed) return;
  installed = false;
  window.removeEventListener('popstate', onPopState, true);
  document.removeEventListener('click', onTap, true);
  router?.off?.('routeWillChange', onRouteWillChange);
  router?.off?.('routeDidChange', onRouteDidChange);
  cancelRestore();
  router = null;
  positions.clear();
  tapped = null;
  popNavigation = false;
  fromHistory = false;
}

/** Test seam. */
function scrollRestoreState() {
  return {
    installed,
    positions: new Map(positions),
    popNavigation,
    fromHistory
  };
}

export { installScrollRestore, nuovaOwnsPageScroll, scrollRestoreState, uninstallScrollRestore };
//# sourceMappingURL=nv-scroll-restore.js.map
